import { useEffect, useEffectEvent, useRef } from "react";
import { pusherClient } from "@/lib/pusher/pusher-client";
import { env } from "@/env";
import { getApiHeaders } from "@/lib/api-helpers";
import { belongsToActiveAttempt } from "./three-ds-attempt";

interface Vgs3DSCompletionResult {
  success: boolean;
  transactionId: string;
  status?: string;
}

interface Vgs3DSPusherEvent {
  xid: string;
  /** Optional for backward compatibility: older events may omit it, and the
   *  attempt-scoping guard deliberately honors key-less events. */
  merchant_transaction_id?: string;
  status: "success" | "failed";
  challenge_status?: string;
  error_message?: string;
}

interface UseVgs3DSCompletionOptions {
  cartToken: string;
  onComplete: (result: Vgs3DSCompletionResult) => void;
  onError: (error: string) => void;
  /** Subscribe to Pusher and start polling when true (i.e. when the 3DS modal is open). */
  enabled?: boolean;
  /** The active attempt's merchant_tx_id — when present, completion signals
   *  for any OTHER attempt are ignored (stale metadata, late webhooks). */
  merchantTxId?: string;
  /** Called when the attempt was canceled (e.g. from another tab) — a quiet,
   *  no-error terminal. */
  onCanceled?: () => void;
}

// Poll interval and timeout for the cart API fallback.
// Polling at 3-second intervals is the reliable secondary check alongside
// the Pusher fast path (the backend broadcasts the result immediately).
// The 6.5-minute timeout sits past the backend safety-net boundary (~6:00),
// so the poll surfaces the backend's verdict — accurate decline, rescued
// approval, or TIMEOUT — instead of the frontend self-declaring first.
const POLL_INTERVAL_MS = 3_000;
const TIMEOUT_MS = 390_000;

interface ThreeDSStatus {
  completed: boolean;
  failed: boolean;
  canceled: boolean;
  merchantTxId?: string;
  status?: string;
  errorMessage?: string;
}

/**
 * Fetch cart metadata directly (bypassing Zod schema) to check 3DS status.
 * The cart API exposes `metadata` via CartBlueprinter but the FE Zod schema
 * strips it. A raw fetch lets us read the 3DS fields without modifying the
 * shared cart schema.
 */
async function check3DSStatus(
  cartToken: string,
): Promise<ThreeDSStatus | null> {
  try {
    const res = await fetch(
      `${env.NEXT_PUBLIC_FLUID_API_URL}/carts/${cartToken}`,
      {
        method: "GET",
        headers: getApiHeaders(),
      },
    );

    if (!res.ok) return null;

    const data = await res.json();
    const metadata = data?.cart?.metadata;
    if (!metadata) return null;

    return {
      completed: metadata.three_ds_completed === true,
      failed: !!metadata.three_ds_failed_at,
      canceled: metadata.three_ds_status === "CANCELED",
      merchantTxId: metadata.three_ds_merchant_tx_id as string | undefined,
      status: metadata.three_ds_status as string | undefined,
      errorMessage: metadata.three_ds_message as string | undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Subscribes to Pusher and polls cart metadata for the 3DS challenge result.
 *
 * Activated by `enabled` — pass `vgsThreeDSModal.isOpen` so the subscription
 * starts as soon as the challenge modal opens. No URL-param dance needed.
 */
export function useVgs3DSCompletion({
  cartToken,
  onComplete,
  onError,
  enabled = false,
  merchantTxId,
  onCanceled,
}: UseVgs3DSCompletionOptions) {
  const hasHandledRef = useRef(false);
  const isSubscribedRef = useRef(false);

  const handleComplete = useEffectEvent((result: Vgs3DSCompletionResult) => {
    onComplete(result);
  });

  const handleError = useEffectEvent((error: string) => {
    onError(error);
  });

  const handleCanceled = useEffectEvent(() => {
    onCanceled?.();
  });

  // Shared handler for Pusher events, polling results, and timeout.
  // hasHandledRef ensures only the first source to resolve wins.
  const handleResult = useEffectEvent(
    (result: {
      status: "success" | "failed";
      errorMessage?: string;
      transactionId?: string;
    }) => {
      if (hasHandledRef.current) return;
      hasHandledRef.current = true;

      if (result.status === "success") {
        handleComplete({
          success: true,
          transactionId: result.transactionId ?? "",
          status: result.status,
        });
      } else {
        handleError(result.errorMessage || "3DS verification failed");
      }
    },
  );

  // Reset handled flag when enabled toggles off (modal closed / new attempt).
  useEffect(() => {
    if (!enabled && hasHandledRef.current) {
      hasHandledRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !cartToken || hasHandledRef.current) {
      return;
    }

    if (isSubscribedRef.current) {
      return;
    }

    let cancelled = false;
    const channelName = `private-cart-${cartToken}`;

    isSubscribedRef.current = true;
    const channel = pusherClient.subscribe(channelName);
    const hasPusher = !!channel;

    if (!hasPusher) {
      console.error(
        "[VGS 3DS] Pusher subscribe returned null — using cart-metadata polling only",
      );
      isSubscribedRef.current = false;
      // intentionally NOT returning — polling + timeout below are the fallback
    }

    // --- Fast path: Pusher event ---
    const handleVgs3DSChallenge = (data: Vgs3DSPusherEvent) => {
      if (cancelled) return;

      // Attempt-scoping: ignore events for a different (older) attempt.
      if (!belongsToActiveAttempt(merchantTxId, data.merchant_transaction_id)) {
        return;
      }

      handleResult({
        status: data.status,
        errorMessage: data.error_message,
        transactionId: data.merchant_transaction_id,
      });
    };

    if (hasPusher) {
      channel.bind("vgs_3ds_challenge", handleVgs3DSChallenge);
    }

    // --- Reliable fallback: poll cart API for 3DS metadata ---
    const pollInterval = setInterval(async () => {
      if (hasHandledRef.current || cancelled) return;

      const status = await check3DSStatus(cartToken);
      if (cancelled || !status) return;

      // Attempt-scoping: stale metadata from a previous attempt must not
      // resolve this modal (the backend wipes per-attempt keys on re-init,
      // but a late webhook can still land mid-retry).
      if (!belongsToActiveAttempt(merchantTxId, status.merchantTxId)) {
        return;
      }

      if (status.completed) {
        handleResult({ status: "success" });
      } else if (status.failed) {
        handleResult({
          status: "failed",
          errorMessage:
            status.errorMessage ||
            `3DS verification ${status.status || "failed"}`,
        });
      } else if (status.canceled) {
        // Canceled (possibly from another tab): quiet no-error terminal.
        if (!hasHandledRef.current) {
          hasHandledRef.current = true;
          handleCanceled();
        }
      }
    }, POLL_INTERVAL_MS);

    // --- Safety net: hard timeout ---
    const timeout = setTimeout(() => {
      if (hasHandledRef.current || cancelled) return;

      console.error("[VGS 3DS] Timed out waiting for 3DS result");
      handleResult({
        status: "failed",
        errorMessage: "Payment verification timed out. Please try again.",
      });
    }, TIMEOUT_MS);

    // Cleanup — cancelled flag prevents in-flight polls from calling handleResult
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      clearInterval(pollInterval);
      if (hasPusher) {
        channel.unbind("vgs_3ds_challenge", handleVgs3DSChallenge);
        pusherClient.unsubscribe(channelName);
      }
      isSubscribedRef.current = false;
    };
  }, [enabled, cartToken, merchantTxId]);
}
